/**
 * Approximate centroid coordinates (lat, lng) for each sub-section / section
 * of the Abidjan "Pharmacies de Garde" directory.
 *
 * Used to compute the nearest pharmacy to the user via Haversine distance.
 * Coordinates are intentionally approximate (~1 km accuracy) — pharmacies
 * within a sub-section share the same centroid.
 */
export const SUBSECTION_COORDS = {
  // SECTION D'ABOBO
  'ABOBO': [5.4167, -4.0500],
  'ABOBO PK 18': [5.4700, -4.0200],
  'ANYAMA': [5.4900, -4.0500],
  'ALEPE / MONTEZO / BROFODOUME': [5.5000, -3.7500],

  // SECTION D'ADJAME
  'ADJAME CENTRE': [5.3580, -4.0229],
  'ATTECOUBE': [5.3400, -4.0400],
  'WILLIAMSVILLE': [5.3410, -4.0150],

  // SECTION DE COCODY
  'BINGERVILLE': [5.3578, -3.8900],
  'ZONE AKOUEDO - PALMERAIE EXTENSION - ABATTA': [5.3660, -3.9280],
  'RIVIERA': [5.3650, -3.9500],
  'COCODY': [5.3510, -3.9850],
  'II PLATEAUX': [5.3830, -3.9750],

  // SECTION DE MARCORY
  'MARCORY NORD': [5.2900, -3.9850],
  'ANOUMABO': [5.2950, -3.9700],
  'MARCORY SUD': [5.2850, -3.9950],

  // SECTION DE PORT BOUET
  'CENTRE': [5.2400, -3.9250],
  'VRIDI': [5.2600, -3.9800],
  'ADJOUFFOU / GONZAQ / ANANI': [5.2500, -3.9000],

  // SECTION DE YOPOUGON
  'ABOBODOUME / LOCODJORO': [5.3340, -4.0750],
  'ALLOKOI PK 23': [5.4600, -4.1400],
  'YOPOUGON': [5.3400, -4.0820],
};

// Fallback centroid per main section when no sub-section is available
export const SECTION_COORDS = {
  "SECTION D'ABOBO": [5.4167, -4.0500],
  "SECTION D'ADJAME": [5.3500, -4.0230],
  'SECTION DE COCODY': [5.3600, -3.9700],
  'SECTION DE KOUMASSI': [5.3000, -3.9580],
  'SECTION DE MARCORY': [5.2900, -3.9880],
  'SECTION DU PLATEAU': [5.3280, -4.0220],
  'SECTION DE PORT BOUET': [5.2400, -3.9250],
  'SECTION DE TREICHVILLE': [5.3000, -4.0050],
  'SECTION DE YOPOUGON': [5.3400, -4.0820],
};

/**
 * Compute Haversine distance between two [lat, lng] points in kilometers.
 */
export function haversineKm(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371; // Earth radius in km
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Get coordinates for a pharmacy entry, using sub-section first, then section.
 */
export function getPharmacyCoords(pharmacy) {
  if (!pharmacy) return null;
  const sub = (pharmacy.sub_section || '').toUpperCase();
  if (sub && SUBSECTION_COORDS[sub]) return SUBSECTION_COORDS[sub];
  const sec = pharmacy.section || '';
  if (sec && SECTION_COORDS[sec]) return SECTION_COORDS[sec];
  return null;
}

// ---------------------------------------------------------------------------
// Home pharmacy — known-accurate GPS coordinates.
// Used for the "Itinéraire" button so Google Maps always resolves the right
// place (plus code 92V7+H8 Abidjan, Angré 7ème Tranche, Carrefour Bluetooth).
// Without this, Google Maps can route to another "Saint Ange" location or
// return "lieu introuvable".
// ---------------------------------------------------------------------------
export const HOME_PHARMACY = {
  nameMatch: /SAINT\s*ANGE\s*DES\s*AMIS/i,
  lat: 5.37361,
  lng: -3.98139,
  placeUrl: 'https://www.google.com/maps/place/92V7+H8+Abidjan',
};

export function isHomePharmacy(pharmacy) {
  return pharmacy && HOME_PHARMACY.nameMatch.test(pharmacy.name || '');
}

/**
 * Build the most reliable Google Maps directions URL for a given pharmacy.
 * Priority:
 *   1. Known-accurate coords for the home pharmacy (Saint Ange Des Amis).
 *   2. Precise geocoded coords (pharmacy.lat / pharmacy.lng).
 *   3. Text query including Abidjan, Côte d'Ivoire as disambiguators.
 */
export function buildDirectionsUrl(pharmacy, userLocation = null) {
  let destination;
  let hasPrecise = false;

  if (isHomePharmacy(pharmacy)) {
    destination = `${HOME_PHARMACY.lat},${HOME_PHARMACY.lng}`;
    hasPrecise = true;
  } else if (Number.isFinite(pharmacy?.lat) && Number.isFinite(pharmacy?.lng)) {
    destination = `${pharmacy.lat},${pharmacy.lng}`;
    hasPrecise = true;
  } else {
    const parts = [
      pharmacy?.name,
      pharmacy?.location,
      pharmacy?.sub_section,
      'Abidjan',
      "Côte d'Ivoire",
    ].filter(Boolean);
    destination = encodeURIComponent(parts.join(', '));
  }
  const origin = userLocation ? `&origin=${userLocation[0]},${userLocation[1]}` : '';
  return {
    url: `https://www.google.com/maps/dir/?api=1&destination=${destination}${origin}`,
    hasPrecise,
  };
}
